import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/posts/${slug}`).then((r) => {
      const data = r.data;
      if (data && typeof data === "object" && !Array.isArray(data) && data.title && data.content) setPost(data);
      else setNotFound(true);
    }).catch(() => setNotFound(true));
  }, [slug]);

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-giva-bg">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-32 lg:pt-40">
        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm font-semibold text-giva-text2 transition-colors hover:text-giva-primary">
          <ArrowLeft className="h-4 w-4" /> {t("post.back")}
        </Link>

        {!post && !notFound && (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-giva-primary" /></div>
        )}

        {notFound && <p className="py-24 text-center text-giva-text2" data-testid="post-not-found">{t("post.notFound")}</p>}

        {post && (
          <article data-testid="blog-article" className="mt-8">
            <span className="text-xs font-semibold uppercase tracking-wider text-giva-accent">{post.category}</span>
            <h1 className="mt-4 font-display text-4xl font-black leading-tight tracking-tight text-giva-navy sm:text-5xl">{post.title}</h1>
            <div className="mt-6 flex items-center gap-3 text-sm text-giva-text2">
              <span>{post.author}</span>
              <span className="h-1 w-1 rounded-full bg-giva-line" />
              <span>{formatDate(post.created_at)}</span>
            </div>
            {post.cover_image && <img src={post.cover_image} alt={post.title} className="mt-10 h-72 w-full rounded-2xl object-cover sm:h-96" />}
            <div className="mt-10 space-y-6">
              {String(post.content || "").split("\n").filter((l) => l.trim()).map((para, i) => (
                <p key={i} className="text-lg leading-relaxed text-giva-text2">{para}</p>
              ))}
            </div>
          </article>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
